import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '../api/client';
import type { WpisDziennika } from '../api/typy';
import { Karta, Ladowanie, Plakietka, Przycisk } from '../components/ui';
import { PrzyciskExcel } from '../components/PrzyciskExcel';
import { dataGodzina } from '../lib/format';

const OPIS_AKCJI: Record<string, string> = {
  CREATE: 'utworzenie', UPDATE: 'zmiana', DELETE: 'usunięcie',
  LOGIN: 'logowanie', LOGIN_FAILED: 'nieudane logowanie', LOGOUT: 'wylogowanie',
  PASSWORD_CHANGE: 'zmiana hasła', EXPORT: 'eksport', OVERRIDE: 'decyzja wyjątkowa',
};

const OPIS_ENCJI: Record<string, string> = {
  user: 'konto', role: 'rola', session: 'sesja', item: 'indeks',
  movement: 'ruch magazynowy', document: 'dokument', reservation: 'rezerwacja',
};

export function Dziennik() {
  const [strona, setStrona] = useState(1);
  const [akcja, setAkcja] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['dziennik', strona, akcja],
    queryFn: () =>
      api.get<{ pozycje: WpisDziennika[]; razem: number; naStronie: number }>(
        `/dziennik?strona=${strona}&naStronie=50${akcja ? `&akcja=${akcja}` : ''}`,
      ),
    // Kilka osób pracuje naraz — dziennik ma pokazywać bieżący stan bez F5.
    refetchInterval: 15_000,
  });

  if (isLoading) return <Ladowanie />;

  const razem = data?.razem ?? 0;
  const stron = Math.max(1, Math.ceil(razem / (data?.naStronie ?? 50)));

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-kamien-900">Dziennik operacji</h1>
          <p className="mt-1 text-sm text-kamien-500">
            Każda operacja z autorem, stanowiskiem i wartościami przed i po.
            Zapisu nie da się zmienić ani skasować — pilnuje tego sama baza.
          </p>
        </div>
        <PrzyciskExcel dzial="dziennik" parametry={new URLSearchParams(akcja ? { akcja } : {})} className="ml-auto" />
        <select
          className="min-h-11 rounded-md border border-kamien-300 bg-white px-3 text-sm"
          value={akcja}
          onChange={(e) => { setAkcja(e.target.value); setStrona(1); }}
          aria-label="Filtr rodzaju operacji"
        >
          <option value="">Wszystkie operacje</option>
          {Object.entries(OPIS_AKCJI).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
      </div>

      <Karta>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[900px] text-sm">
            <thead className="border-b border-kamien-200 text-left text-xs uppercase tracking-wide text-kamien-500">
              <tr>
                <th className="px-5 py-3 font-medium">Kiedy</th>
                <th className="px-5 py-3 font-medium">Kto</th>
                <th className="px-5 py-3 font-medium">Operacja</th>
                <th className="px-5 py-3 font-medium">Czego dotyczy</th>
                <th className="px-5 py-3 font-medium">Stanowisko</th>
                <th className="px-5 py-3 font-medium">Szczegóły</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-kamien-200">
              {data?.pozycje.map((w) => (
                <tr key={w.id}>
                  <td className="whitespace-nowrap px-5 py-3 text-kamien-600">{dataGodzina(w.at)}</td>
                  <td className="px-5 py-3">
                    {w.user ? (
                      <span className="text-kamien-900">
                        {w.user.fullName}{' '}
                        <span className="text-kamien-500">({w.user.login})</span>
                      </span>
                    ) : (
                      <span className="text-kamien-400">—</span>
                    )}
                  </td>
                  <td className="px-5 py-3">
                    <Plakietka ton={w.action === 'LOGIN_FAILED' ? 'uwaga' : 'neutralny'}>
                      {OPIS_AKCJI[w.action] ?? w.action}
                    </Plakietka>
                  </td>
                  <td className="px-5 py-3 text-kamien-700">
                    {OPIS_ENCJI[w.entity] ?? w.entity}
                    {w.entityId && <span className="text-kamien-400"> #{w.entityId}</span>}
                  </td>
                  <td className="px-5 py-3 font-mono text-xs text-kamien-500">
                    {w.ipAddress ?? '—'}
                  </td>
                  <td className="max-w-md px-5 py-3">
                    {w.reason && <p className="text-kamien-700">{w.reason}</p>}
                    {(w.before || w.after) ? (
                      <details className="text-xs text-kamien-500">
                        <summary className="cursor-pointer select-none hover:text-bordo-600">
                          przed / po
                        </summary>
                        <pre className="mt-1 max-h-48 overflow-auto rounded bg-kamien-100 p-2 text-[11px] leading-relaxed">
{JSON.stringify({ przed: w.before, po: w.after }, null, 2)}
                        </pre>
                      </details>
                    ) : (!w.reason && <span className="text-kamien-400">—</span>)}
                  </td>
                </tr>
              ))}
              {data?.pozycje.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-kamien-500">
                  Brak wpisów dla wybranego filtru.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between border-t border-kamien-200 px-5 py-3 text-sm text-kamien-600">
          <span>{razem} {razem === 1 ? 'wpis' : 'wpisów'} · strona {strona} z {stron}</span>
          <div className="flex gap-2">
            <Przycisk className="min-h-9 text-xs" disabled={strona <= 1}
              onClick={() => setStrona((s) => s - 1)}>Poprzednia</Przycisk>
            <Przycisk className="min-h-9 text-xs" disabled={strona >= stron}
              onClick={() => setStrona((s) => s + 1)}>Następna</Przycisk>
          </div>
        </div>
      </Karta>
    </div>
  );
}
